import { Container, FormLabel, Grid, IconButton, InputAdornment, TextField } from '@material-ui/core'
import PersonIcon from '@mui/icons-material/Person'
import { Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import logo from '../../assests/Logos/tm2u_logo_150.png'
import { ButtonSpinner } from '../custom'
import { buildSSOUrl, SSO_PATIENT_ID_KEY } from '../../utilities'
import './signin.scss'
import bg1 from '../../assests/images/bg1.png'
import bg2 from '../../assests/images/bg2.png'
import bg3 from '../../assests/images/bg3.png'
import bg4 from '../../assests/images/bg4.png'
import bg5 from '../../assests/images/bg5.png'

const bgImages = [bg1, bg2, bg3, bg4, bg5]
// Computation
const randomBgImage = bgImages[Math.floor(Math.random() * bgImages.length)]

const SSO_ERROR_MESSAGES = {
    not_found:
        "We couldn't find a patient with that Patient ID. Please check it and try again, or call us at (562) 268-0955.",
    expired: 'Your sign-in link has expired. Please try again.',
    invalid: "We couldn't verify your sign-in. Please try again.",
    server_error:
        "We couldn't sign you in right now. Please try again, or call us at (562) 268-0955.",
}

// Calls the getSSO link in the background before leaving the app. A valid Patient ID answers with a
// redirect; failures answer with JSON (CORS is open), so they can be shown here instead of on Mirth.
// Returns '' when the link is good, otherwise a SSO_ERROR_MESSAGES key.
const checkSSOUrl = async (url) => {
    const res = await fetch(url, { redirect: 'manual' })
    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) return ''
    if (res.status === 410) return 'expired'
    if (res.status === 401) return 'invalid'
    const body = await res.json().catch(() => ({}))
    // No patient for this ID in AdvancedMD: the channel fails reading the patient's @name
    if (res.status === 500 && /@name/.test(body?.message || '')) return 'not_found'
    return 'server_error'
}

const PatientIdMagicLink = () => {
    const [ssoError, setSsoError] = React.useState('')
    const validateSchema = Yup.object().shape({
        patient_id: Yup.string().trim().required('Patient ID is required'),
    })

    React.useEffect(() => {
        // A failed/abandoned SSO attempt must not leave its Patient ID behind for the booking flow
        sessionStorage.removeItem(SSO_PATIENT_ID_KEY)
    }, [])

    return (
        <Container className="sign-in-wrapper">
            <Grid container className="form-wrapper">
                <Grid item xs={12} sm={6} span={12}>
                    <div
                        className="form-img"
                        style={{
                            background: `url(${randomBgImage})`,
                            borderRadius: '10px',
                            backgroundRepeat: 'none',
                            backgroundSize: 'cover',
                        }}></div>
                </Grid>
                <Grid item xs={12} sm={6} span={12} ml={2} className="right-panel">
                    <Grid className="logo-wrapper">
                        <img className="form-logo" src={logo} alt="TeleMed2U"></img>
                    </Grid>
                    <Formik
                        initialValues={{ patient_id: '' }}
                        validationSchema={validateSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            const patientId = values.patient_id.trim()
                            setSsoError('')
                            try {
                                const ssoUrl = buildSSOUrl(patientId)
                                const errorCode = await checkSSOUrl(ssoUrl)
                                if (errorCode) {
                                    setSsoError(SSO_ERROR_MESSAGES[errorCode])
                                    setSubmitting(false)
                                    return
                                }
                                // Remembered for the booking flow: after SSO, findPatient is called with this ID
                                sessionStorage.setItem(SSO_PATIENT_ID_KEY, patientId)
                                // Redirect to the getSSO channel; it 301s back to /verify-magic-link
                                window.location.href = ssoUrl
                            } catch (error) {
                                setSsoError(SSO_ERROR_MESSAGES.server_error)
                                setSubmitting(false)
                            }
                        }}>
                        {({ errors, touched, values, isSubmitting, handleChange, handleSubmit }) => (
                            <form className="form registration" onSubmit={handleSubmit} noValidate>
                                <div className="inner-wrapper-logo">
                                    <img className="mobile-logo" src={logo} alt="TeleMed2U"></img>
                                </div>
                                <h1>Sign in to your patient portal!</h1>
                                {ssoError && (
                                    <p role="alert" style={{ color: '#E81A1A', marginBottom: '12px' }}>
                                        {ssoError}
                                    </p>
                                )}
                                <FormLabel className="input-label" component="legend">
                                    Patient ID
                                </FormLabel>
                                <TextField
                                    id="magic-link-patient-id"
                                    required
                                    error={Boolean(errors.patient_id && touched.patient_id)}
                                    helperText={
                                        errors.patient_id && touched.patient_id
                                            ? errors.patient_id
                                            : ''
                                    }
                                    variant="outlined"
                                    size="small"
                                    className="sign-in-text-input"
                                    name="patient_id"
                                    placeholder="Enter your Patient ID"
                                    value={values.patient_id}
                                    onChange={handleChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconButton aria-label="patient id" edge="start">
                                                    <PersonIcon
                                                        style={{
                                                            color: '#667689',
                                                            fontSize: '12px',
                                                            marginLeft: '5px',
                                                        }}
                                                    />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <div className="submit">
                                    <ButtonSpinner
                                        disabled={!values.patient_id || Boolean(isSubmitting)}
                                        isLoading={Boolean(isSubmitting)}
                                        label={'Login'}
                                        className="send-magic-submit"
                                        sx={{
                                            width: '100%',
                                            opacity: !values.patient_id ? '0.7' : 1,
                                        }}
                                    />
                                </div>
                            </form>
                        )}
                    </Formik>
                    <Grid className="form-footer">
                        <p className="footer-tag">
                            If you have any questions or need assistance, please call us at{' '}
                            <a>(562) 268-0955</a>.{' '}
                        </p>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

export default PatientIdMagicLink
